/* global Excel console */

import { fetchMockSalesData } from "../shared/mockData";
import { createInlineSparkline } from "./taskpane";
import { STORAGE_KEYS, EXCEL_DATE } from "../shared/constants";

/**
 * Helper function to set borders on all edges of a range
 */
export function setBordersOnRange(range: Excel.Range, color: string, weight: Excel.BorderWeight) {
  const topBorder = range.format.borders.getItem("EdgeTop");
  topBorder.style = "Continuous";
  topBorder.color = color;
  topBorder.weight = weight;

  const bottomBorder = range.format.borders.getItem("EdgeBottom");
  bottomBorder.style = "Continuous";
  bottomBorder.color = color;
  bottomBorder.weight = weight;

  const leftBorder = range.format.borders.getItem("EdgeLeft");
  leftBorder.style = "Continuous";
  leftBorder.color = color;
  leftBorder.weight = weight;

  const rightBorder = range.format.borders.getItem("EdgeRight");
  rightBorder.style = "Continuous";
  rightBorder.color = color;
  rightBorder.weight = weight;
}

function parseDateValue(value: any): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number" && isFinite(value)) {
    const excelEpoch = Date.UTC(EXCEL_DATE.EPOCH_YEAR, EXCEL_DATE.EPOCH_MONTH, EXCEL_DATE.EPOCH_DAY);
    const ms = Math.round(value * EXCEL_DATE.MS_PER_DAY);
    const d = new Date(excelEpoch + ms);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "string") {
    let str = value.trim();
    if (!str) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(str)) {
      const firstSpaceIdx = str.indexOf(" ");
      if (firstSpaceIdx > 0) {
        str = str.slice(0, firstSpaceIdx) + "T" + str.slice(firstSpaceIdx + 1);
      }

      const dotIdx = str.indexOf(".");
      if (dotIdx > -1) {
        str = str.slice(0, dotIdx);
      }
    }

    let d = new Date(str);
    if (!isNaN(d.getTime())) {
      return d;
    }

    if (str.length >= 10) {
      const justDate = str.slice(0, 10);
      d = new Date(justDate);
      if (!isNaN(d.getTime())) {
        return d;
      }
    }
  }

  return null;
}

function getMonthString(dateValue: any) {
  const date = parseDateValue(dateValue);
  if (!date) {
    throw new Error("Invalid date value for month extraction");
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

async function deleteOldTable(
  sheet: Excel.Worksheet,
  tableName: string,
  context: Excel.RequestContext
) {
  try {
    const oldTable = sheet.tables.getItem(tableName);
    oldTable.delete();
    await context.sync();
    console.log(`🗑️ Deleted old table: ${tableName}`);
  } catch (e) {
    console.log(`🗑️ Table not found: ${tableName}`);
  }
}

async function deleteOldChart(
  sheet: Excel.Worksheet,
  chartName: string,
  context: Excel.RequestContext
) {
  try {
    const charts = sheet.charts;
    charts.load("items/name");
    await context.sync();

    for (let i = 0; i < charts.items.length; i++) {
      if (charts.items[i].name === chartName) {
        charts.items[i].delete();
        await context.sync();
        console.log(`🗑️ Deleted old chart: ${chartName}`);
        break;
      }
    }
  } catch (e) {
    console.log(`🗑️ Chart not found: ${chartName}`);
  }
}

function getColumnValue(row: any[], colIdx: number, defaultValue: any = null): any {
  if (colIdx === -1 || colIdx >= row.length) {
    return defaultValue;
  }
  const value = row[colIdx];
  return value !== null && value !== undefined ? value : defaultValue;
}

function getNumericValue(row: any[], colIdx: number, defaultValue: number = 0): number {
  const value = getColumnValue(row, colIdx, defaultValue);
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

export async function createEnhancedSalesPerformanceDashboard() {
  try {
    const salesDataResponse = await fetchMockSalesData();

    if (!salesDataResponse.columns || !salesDataResponse.data) {
      throw new Error("Invalid API response: missing columns or data");
    }

    const columns = salesDataResponse.columns;
    const rawData = salesDataResponse.data;

    console.log("📊 API Response:", {
      totalRows: rawData.length,
      columns: columns.length,
      columnNames: columns,
    });

    let filterParams: {
      skus?: string[];
      start_purchase_date?: string;
      end_purchase_date?: string;
    } | null = null;
    try {
      const stored = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASH_FILTER);
      if (stored) {
        filterParams = JSON.parse(stored);
      }
    } catch (_) {
      // Ignore storage errors and proceed unfiltered
    }

    const hasSkuFilter = !!(
      filterParams &&
      Array.isArray(filterParams.skus) &&
      filterParams.skus.length > 0
    );
    const startDate = filterParams?.start_purchase_date
      ? new Date(filterParams.start_purchase_date)
      : null;
    const endDate = filterParams?.end_purchase_date
      ? new Date(filterParams.end_purchase_date)
      : null;
    const hasStart = !!(startDate && !isNaN(startDate.getTime()));
    const hasEnd = !!(endDate && !isNaN(endDate.getTime()));
    const normalizedSkuSet = new Set(
      (filterParams?.skus || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean)
    );

    const dateOfSaleIdx = columns.indexOf("date_of_sale");
    const statusIdIdx = columns.indexOf("status_id");
    const salePriceIdx = columns.indexOf("sale_price");
    const qtySoldIdx = columns.indexOf("qty_sold");
    const variationSkuNameIdx = columns.indexOf("variation_sku_name");
    const upcIdx = columns.indexOf("upc");
    const titleIdx = columns.indexOf("title");
    const sourceOfSaleIdx = columns.indexOf("source_of_sale");

    // Validate that critical columns exist
    console.log("🔍 Column indices:", {
      dateOfSale: dateOfSaleIdx,
      statusId: statusIdIdx,
      salePrice: salePriceIdx,
      qtySold: qtySoldIdx,
      variationSkuName: variationSkuNameIdx,
      upc: upcIdx,
      title: titleIdx,
      sourceOfSale: sourceOfSaleIdx,
    });

    if (salePriceIdx === -1) {
      throw new Error("Critical column missing: sale_price is required for dashboard generation");
    }
    if (qtySoldIdx === -1) {
      throw new Error("Critical column missing: qty_sold is required for dashboard generation");
    }
    if (dateOfSaleIdx === -1) {
      console.warn(
        "⚠️ Warning: date_of_sale column not found - time-based charts may not work correctly"
      );
    }

    // Filter data based on criteria
    const validRows: any[][] = [];
    let filteredOutCancelled = 0;
    let filteredOutDate = 0;
    let filteredOutSku = 0;

    for (const row of rawData) {
      // Filter cancelled orders (status_id === 3)
      if (statusIdIdx >= 0 && row[statusIdIdx] === 3) {
        filteredOutCancelled++;
        continue;
      }

      // Optional date filtering
      if (hasStart || hasEnd) {
        if (dateOfSaleIdx < 0) {
          filteredOutDate++;
          continue;
        }
        const dateValueRaw = getColumnValue(row, dateOfSaleIdx);
        const purchaseDate = parseDateValue(dateValueRaw);
        if (!purchaseDate) {
          filteredOutDate++;
          continue;
        }
        if (hasStart && purchaseDate < startDate!) {
          filteredOutDate++;
          continue;
        }
        if (hasEnd && purchaseDate > endDate!) {
          filteredOutDate++;
          continue;
        }
      }

      if (hasSkuFilter) {
        const skuFromVariation = getColumnValue(row, variationSkuNameIdx, "");
        const skuFromUpc = getColumnValue(row, upcIdx, "");
        const sku = skuFromVariation || skuFromUpc;
        const orderSku = String(sku || "")
          .trim()
          .toLowerCase();
        if (!normalizedSkuSet.has(orderSku)) {
          filteredOutSku++;
          continue;
        }
      }

      validRows.push(row);
    }

    console.log("✅ Filtering complete:", {
      totalRows: rawData.length,
      validRows: validRows.length,
      filteredCancelled: filteredOutCancelled,
      filteredDate: filteredOutDate,
      filteredSku: filteredOutSku,
    });

    if (validRows.length === 0) {
      throw new Error(
        "No valid data found after filtering. Please check your filters or data source."
      );
    }

    await Excel.run(async (context) => {
      console.log("🚀 Starting enhanced dashboard creation with real API data...");

      let dashboardSheet: Excel.Worksheet;
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();

      let dashboardExists = false;
      worksheets.items.forEach((sheet) => {
        if (sheet.name === "Dashboard") {
          dashboardExists = true;
          dashboardSheet = sheet;
        }
      });

      if (dashboardExists) {
        console.log("📝 Dashboard worksheet exists, deleting...");
        dashboardSheet!.delete();
        await context.sync();
      }

      console.log("➕ Creating new Dashboard worksheet...");
      dashboardSheet = worksheets.add("Dashboard");
      dashboardSheet.activate();
      await context.sync();

      // ============ TITLE SECTION ============
      const titleRange = dashboardSheet.getRange("A1:H2");
      titleRange.merge();
      await context.sync();

      dashboardSheet.getRange("A1").values = [["Sales Performance Dashboard"]];
      titleRange.format.font.size = 18;
      titleRange.format.font.bold = true;
      titleRange.format.font.color = "#1F4788";
      titleRange.format.horizontalAlignment = "Center";
      titleRange.format.verticalAlignment = "Center";
      titleRange.format.fill.color = "#F0F4F8";

      // ============ THIRD ROW FOR FILTER INFORMATION ============
      const thirdRowRange = dashboardSheet.getRange("A3:H3");
      thirdRowRange.format.fill.color = "#F0F4F8";

      // Get filter parameters from storage for Sales dashboard (display only)
      const filterParamsForDisplay = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASH_FILTER);

      if (filterParamsForDisplay) {
        const params = JSON.parse(filterParamsForDisplay);

        const formatDisplayDate = (iso: string) => {
          try {
            const d = new Date(iso);
            if (!isNaN(d.getTime())) {
              return d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              });
            }
          } catch (_) {}
          return "";
        };

        let filterParts = [];

        if (params.skus && params.skus.length > 0) {
          const skuText = params.skus.join(", ");
          filterParts.push(`Filtered SKUs: ${skuText}`);
        }

        if (params.start_purchase_date || params.end_purchase_date) {
          const startText = params.start_purchase_date
            ? formatDisplayDate(params.start_purchase_date)
            : "";
          const endText = params.end_purchase_date
            ? formatDisplayDate(params.end_purchase_date)
            : "";

          if (startText && endText) {
            filterParts.push(`Date Range: ${startText} - ${endText}`);
          } else if (startText) {
            filterParts.push(`Date From: ${startText}`);
          } else if (endText) {
            filterParts.push(`Date Until: ${endText}`);
          }
        }

        if (filterParts.length > 0) {
          const filterText = filterParts.join(" | ");

          const centeredRange = dashboardSheet.getRange("A3:H3");
          centeredRange.merge();
          dashboardSheet.getRange("A3").values = [[filterText]];
          centeredRange.format.font.size = 10;
          centeredRange.format.font.italic = true;
          centeredRange.format.font.color = "#666666";
          centeredRange.format.horizontalAlignment = "Center";
          centeredRange.format.verticalAlignment = "Center";
          centeredRange.format.fill.color = "#F0F4F8";
        } else {
          try {
            dashboardSheet.getRange("A3:H3").unmerge();
          } catch (e) {
            /* ignore if not merged */
          }
          dashboardSheet.getRange("A3:H3").clear(Excel.ClearApplyTo.contents);
        }
      } else {
        try {
          dashboardSheet.getRange("A3:H3").unmerge();
        } catch (e) {
          /* ignore */
        }
        dashboardSheet.getRange("A3:H3").clear(Excel.ClearApplyTo.contents);
      }

      // ============ CALCULATE KPIs ============
      // Calculate total revenue from sale_price
      const totalRevenue = validRows.reduce((acc, row) => {
        const amount = getNumericValue(row, salePriceIdx, 0);
        return acc + amount;
      }, 0);
      const totalRevenueFormatted = totalRevenue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });

      const totalUnits = validRows.reduce((acc, row) => {
        const qty = getNumericValue(row, qtySoldIdx, 0);
        return acc + qty;
      }, 0);
      const totalUnitsFormatted = totalUnits.toLocaleString("en-US");

      const dates = validRows
        .map((row) => {
          const dateValue = getColumnValue(row, dateOfSaleIdx);
          return parseDateValue(dateValue);
        })
        .filter((d) => d !== null);

      let months = 1;
      if (dates.length > 0) {
        const minDate = new Date(Math.min(...dates.map((d) => d!.getTime())));
        const maxDate = new Date(Math.max(...dates.map((d) => d!.getTime())));
        const monthDiff =
          (maxDate.getFullYear() - minDate.getFullYear()) * 12 +
          (maxDate.getMonth() - minDate.getMonth()) +
          1;
        months = Math.max(1, monthDiff);
      }

      const avgVelocity = totalUnits / months;
      const avgVelocityFormatted = avgVelocity.toLocaleString("en-US", {
        maximumFractionDigits: 1,
      });

      // Group by title (product) for category analysis - using title as product identifier
      const productSales: { [product: string]: number } = {};
      for (const row of validRows) {
        const product = getColumnValue(row, titleIdx, "Unknown");
        const amount = getNumericValue(row, salePriceIdx, 0);
        if (!productSales[product]) {
          productSales[product] = 0;
        }
        productSales[product] += amount;
      }

      let bestSellingProduct = "N/A";
      let maxSales = 0;
      for (const [product, sales] of Object.entries(productSales)) {
        if (sales > maxSales) {
          maxSales = sales;
          bestSellingProduct = product;
        }
      }

      let bestProductPercent = 0;
      if (totalRevenue > 0 && productSales[bestSellingProduct]) {
        bestProductPercent = (productSales[bestSellingProduct] / totalRevenue) * 100;
      }
      const bestProductPercentFormatted = bestProductPercent.toFixed(1);

      // ============ KPI CARDS SECTION (Row 4-6) - 4 cards within A:H ============
      const kpi1Range = dashboardSheet.getRange("A4:B6");
      kpi1Range.merge();

      const kpi2Range = dashboardSheet.getRange("C4:D6");
      kpi2Range.merge();

      const kpi3Range = dashboardSheet.getRange("E4:F6");
      kpi3Range.merge();

      const kpi4Range = dashboardSheet.getRange("G4:H6");
      kpi4Range.merge();

      await context.sync();

      dashboardSheet.getRange("A4").values = [[`Total Revenue\n${totalRevenueFormatted}`]];
      kpi1Range.format.fill.color = "#E6F4EA";
      setBordersOnRange(kpi1Range, "#28A745", Excel.BorderWeight.thick);
      kpi1Range.format.horizontalAlignment = "Center";
      kpi1Range.format.verticalAlignment = "Center";
      kpi1Range.format.font.size = 13;

      dashboardSheet.getRange("C4").values = [[`Units Sold\n${totalUnitsFormatted}`]];
      kpi2Range.format.fill.color = "#E3F2FD";
      setBordersOnRange(kpi2Range, "#2196F3", Excel.BorderWeight.thick);
      kpi2Range.format.horizontalAlignment = "Center";
      kpi2Range.format.verticalAlignment = "Center";
      kpi2Range.format.font.size = 13;

      dashboardSheet.getRange("E4").values = [[`Avg Velocity\n${avgVelocityFormatted} / Month`]];
      kpi3Range.format.fill.color = "#FFF8E1";
      setBordersOnRange(kpi3Range, "#FFC107", Excel.BorderWeight.thick);
      kpi3Range.format.horizontalAlignment = "Center";
      kpi3Range.format.verticalAlignment = "Center";
      kpi3Range.format.font.size = 13;

      dashboardSheet.getRange("G4").values = [
        [
          `Top Product\n${bestSellingProduct.substring(0, 25)}${bestSellingProduct.length > 25 ? "..." : ""}\n${bestProductPercentFormatted}%`,
        ],
      ];
      kpi4Range.format.fill.color = "#F3E5F5";
      setBordersOnRange(kpi4Range, "#9C27B0", Excel.BorderWeight.thick);
      kpi4Range.format.horizontalAlignment = "Center";
      kpi4Range.format.verticalAlignment = "Center";
      kpi4Range.format.font.size = 13;

      await context.sync();

      // ============ CHART LAYOUT CONFIGURATION ============
      const ROW_HEIGHT = 22;
      let currentRow = 8;

      // Set wider column widths for better chart visibility (within A:H)
      dashboardSheet.getRange("A:H").format.columnWidth = 100;

      // ============ CHART 1: WATERFALL CHART - Revenue Contribution by Top Products (LEFT HALF) ============
      console.log("📈 Creating Chart 1: Waterfall Chart - Revenue Contribution by Products");

      const productRevenueWaterfall: { [product: string]: number } = {};
      for (const row of validRows) {
        const product = getColumnValue(row, titleIdx, "Unknown");
        const amount = getNumericValue(row, salePriceIdx, 0);
        if (!productRevenueWaterfall[product]) {
          productRevenueWaterfall[product] = 0;
        }
        productRevenueWaterfall[product] += amount;
      }

      const topProducts = Object.entries(productRevenueWaterfall)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      const top10Total = topProducts.reduce((sum, [_, revenue]) => sum + revenue, 0);
      const othersRevenue = totalRevenue - top10Total;

      const waterfallHeaders = [["Category", "Revenue"]];
      const waterfallDataValues: any[][] = [];

      topProducts.forEach(([product, revenue]) => {
        const shortProduct = product.substring(0, 30) + (product.length > 30 ? "..." : "");
        waterfallDataValues.push([shortProduct, revenue]);
      });

      if (othersRevenue > 0) {
        waterfallDataValues.push(["Others", othersRevenue]);
      }

      if (waterfallDataValues.length > 0) {
        const waterfallDataRows = waterfallDataValues.length;
        const waterfallEndRow = 7 + waterfallDataRows;

        const clearRange1 = dashboardSheet.getRange(`AA7:AB${waterfallEndRow + 10}`);
        clearRange1.clear(Excel.ClearApplyTo.all);
        await context.sync();

        await deleteOldTable(dashboardSheet, "WaterfallData", context);
        await deleteOldChart(dashboardSheet, "RevenueWaterfallChart", context);

        dashboardSheet.getRange("AA7:AB7").values = waterfallHeaders;
        dashboardSheet.getRange(`AA8:AB${waterfallEndRow}`).values = waterfallDataValues;

        const waterfallTableRange = dashboardSheet.getRange(`AA7:AB${waterfallEndRow}`);
        const waterfallTable = dashboardSheet.tables.add(waterfallTableRange, true);
        waterfallTable.name = "WaterfallData";
        waterfallTable.style = "TableStyleLight1";

        const waterfallHeaderRange = dashboardSheet.getRange("AA7:AB7");
        waterfallHeaderRange.format.fill.color = "#E7E6E6";
        waterfallHeaderRange.format.font.bold = true;
        waterfallHeaderRange.format.font.size = 10;

        await context.sync();

        const waterfallChart = dashboardSheet.charts.add(
          Excel.ChartType.waterfall,
          waterfallTable.getRange(),
          Excel.ChartSeriesBy.columns
        );
        waterfallChart.name = "RevenueWaterfallChart";
        waterfallChart.title.text = "Revenue Contribution by Product";
        waterfallChart.setPosition(`A${currentRow}`, `D${currentRow + ROW_HEIGHT}`);
        waterfallChart.axes.categoryAxis.title.text = "Product";
        waterfallChart.axes.valueAxis.title.text = "Revenue ($)";

        await context.sync();
        console.log("✅ Chart 1: Revenue Waterfall Chart created");
      }

      // ============ CHART 2: HORIZONTAL BAR CHART - Top 10 Products by Revenue (RIGHT HALF) ============
      console.log("📊 Creating Chart 2: Horizontal Bar - Top Products");

      const productRevenueChart2: { [product: string]: number } = {};
      for (const row of validRows) {
        const product = getColumnValue(row, titleIdx, "Unknown");
        const amount = getNumericValue(row, salePriceIdx, 0);
        if (!productRevenueChart2[product]) {
          productRevenueChart2[product] = 0;
        }
        productRevenueChart2[product] += amount;
      }

      const topProductsHeaders = [["Product", "Revenue"]];
      const topProductsDataValues = Object.entries(productRevenueChart2)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([product, revenue]) => [
          product.substring(0, 40) + (product.length > 40 ? "..." : ""),
          revenue,
        ]);

      const topProductsDataRows = topProductsDataValues.length;
      const topProductsEndRow = 7 + topProductsDataRows;

      const clearRange2 = dashboardSheet.getRange(`AD7:AE${topProductsEndRow + 10}`);
      clearRange2.clear(Excel.ClearApplyTo.all);
      await context.sync();

      await deleteOldTable(dashboardSheet, "TopProductsData", context);
      await deleteOldChart(dashboardSheet, "TopProductsBarChart", context);

      dashboardSheet.getRange("AD7:AE7").values = topProductsHeaders;
      dashboardSheet.getRange(`AD8:AE${topProductsEndRow}`).values = topProductsDataValues;

      const topProductsTableRange = dashboardSheet.getRange(`AD7:AE${topProductsEndRow}`);
      const topProductsTable = dashboardSheet.tables.add(topProductsTableRange, true);
      topProductsTable.name = "TopProductsData";
      topProductsTable.style = "TableStyleLight1";

      const topProductsHeaderRange = dashboardSheet.getRange("AD7:AE7");
      topProductsHeaderRange.format.fill.color = "#E7E6E6";
      topProductsHeaderRange.format.font.bold = true;
      topProductsHeaderRange.format.font.size = 10;

      await context.sync();

      const topProductsChart = dashboardSheet.charts.add(
        Excel.ChartType.barClustered,
        topProductsTable.getRange(),
        Excel.ChartSeriesBy.columns
      );
      topProductsChart.name = "TopProductsBarChart";
      topProductsChart.title.text = "Top 10 Products by Revenue";
      topProductsChart.setPosition(`E${currentRow}`, `H${currentRow + ROW_HEIGHT}`);
      topProductsChart.axes.categoryAxis.title.text = "Products";
      topProductsChart.axes.valueAxis.title.text = "Revenue ($)";

      await context.sync();
      console.log("✅ Chart 2: Top Products created");

      currentRow += ROW_HEIGHT + 2;

      // ============ CHART 3: DOUGHNUT CHART - Sales Channel Distribution (LEFT HALF) ============
      console.log("🍩 Creating Chart 3: Doughnut - Sales Channel Distribution");

      const channelRevenue: Record<string, number> = {};
      validRows.forEach((row) => {
        const channel = getColumnValue(row, sourceOfSaleIdx, "Unknown");
        const amount = getNumericValue(row, salePriceIdx, 0);
        const key = channel || "Unknown";
        channelRevenue[key] = (channelRevenue[key] || 0) + amount;
      });

      const channelHeaders = [["Channel", "Revenue"]];
      const channelDataValues = Object.entries(channelRevenue)
        .sort((a, b) => b[1] - a[1])
        .map(([channel, revenue]) => [channel, revenue]);

      if (channelDataValues.length > 0) {
        const channelDataRows = channelDataValues.length;
        const channelEndRow = 7 + channelDataRows;

        const clearRange3 = dashboardSheet.getRange(`AG7:AH${channelEndRow + 10}`);
        clearRange3.clear(Excel.ClearApplyTo.all);
        await context.sync();

        await deleteOldTable(dashboardSheet, "ChannelData", context);
        await deleteOldChart(dashboardSheet, "ChannelDoughnutChart", context);

        dashboardSheet.getRange("AG7:AH7").values = channelHeaders;
        dashboardSheet.getRange(`AG8:AH${channelEndRow}`).values = channelDataValues;

        const channelTableRange = dashboardSheet.getRange(`AG7:AH${channelEndRow}`);
        const channelTable = dashboardSheet.tables.add(channelTableRange, true);
        channelTable.name = "ChannelData";
        channelTable.style = "TableStyleLight1";

        const channelHeaderRange = dashboardSheet.getRange("AG7:AH7");
        channelHeaderRange.format.fill.color = "#E7E6E6";
        channelHeaderRange.format.font.bold = true;
        channelHeaderRange.format.font.size = 10;

        await context.sync();

        const channelChart = dashboardSheet.charts.add(
          Excel.ChartType.doughnut,
          channelTable.getRange(),
          Excel.ChartSeriesBy.columns
        );
        channelChart.name = "ChannelDoughnutChart";
        channelChart.title.text = "Revenue by Sales Channel";
        channelChart.setPosition(`A${currentRow}`, `D${currentRow + ROW_HEIGHT}`);
        channelChart.legend.visible = true;
        channelChart.legend.position = Excel.ChartLegendPosition.right;

        await context.sync();
        console.log("✅ Chart 3: Sales Channel Distribution created");
      }

      // ============ CHART 4: COMBO CHART - Units vs Revenue per Unit (RIGHT HALF) ============
      console.log("📉 Creating Chart 4: Combo - Units vs Revenue per Unit");

      const productUnitsChart4: { [product: string]: number } = {};
      const productRevenueChart4: { [product: string]: number } = {};

      for (const row of validRows) {
        const product = getColumnValue(row, titleIdx, "Unknown");
        const units = getNumericValue(row, qtySoldIdx, 0);
        const revenue = getNumericValue(row, salePriceIdx, 0);

        if (!productUnitsChart4[product]) productUnitsChart4[product] = 0;
        if (!productRevenueChart4[product]) productRevenueChart4[product] = 0;

        productUnitsChart4[product] += units;
        productRevenueChart4[product] += revenue;
      }

      const unitsRevenueData = Object.keys(productUnitsChart4).map((product) => {
        const units = productUnitsChart4[product];
        const revenue = productRevenueChart4[product];
        const revenuePerUnit = units > 0 ? revenue / units : 0;
        return { product, units, revenuePerUnit };
      });

      const comboHeaders = [["Product", "Units Sold", "Revenue per Unit"]];
      const comboDataValues = unitsRevenueData
        .sort((a, b) => b.units - a.units)
        .slice(0, 10)
        .map((item) => [
          item.product.substring(0, 30) + (item.product.length > 30 ? "..." : ""),
          item.units,
          item.revenuePerUnit,
        ]);

      const comboDataRows = comboDataValues.length;
      const comboEndRow = 7 + comboDataRows;

      const clearRange4 = dashboardSheet.getRange(`AJ7:AL${comboEndRow + 10}`);
      clearRange4.clear(Excel.ClearApplyTo.all);
      await context.sync();

      await deleteOldTable(dashboardSheet, "ComboData", context);
      await deleteOldChart(dashboardSheet, "UnitsRevenueComboChart", context);

      dashboardSheet.getRange("AJ7:AL7").values = comboHeaders;
      dashboardSheet.getRange(`AJ8:AL${comboEndRow}`).values = comboDataValues;

      const comboTableRange = dashboardSheet.getRange(`AJ7:AL${comboEndRow}`);
      const comboTable = dashboardSheet.tables.add(comboTableRange, true);
      comboTable.name = "ComboData";
      comboTable.style = "TableStyleLight1";

      const comboHeaderRange = dashboardSheet.getRange("AJ7:AL7");
      comboHeaderRange.format.fill.color = "#E7E6E6";
      comboHeaderRange.format.font.bold = true;
      comboHeaderRange.format.font.size = 10;

      await context.sync();

      const comboChart = dashboardSheet.charts.add(
        Excel.ChartType.columnClustered,
        comboTable.getRange(),
        Excel.ChartSeriesBy.columns
      );
      comboChart.name = "UnitsRevenueComboChart";
      comboChart.title.text = "Units Sold vs Revenue per Unit";
      comboChart.setPosition(`E${currentRow}`, `H${currentRow + ROW_HEIGHT}`);
      comboChart.axes.categoryAxis.title.text = "Products";
      comboChart.axes.valueAxis.title.text = "Units / Revenue";

      await context.sync();

      const comboSeries = comboChart.series;
      comboSeries.getItemAt(0).name = "Units Sold";
      comboSeries.getItemAt(1).name = "Revenue per Unit";
      comboSeries.getItemAt(1).chartType = Excel.ChartType.lineMarkers;

      await context.sync();
      console.log("✅ Chart 4: Units vs Revenue per Unit created");

      currentRow += ROW_HEIGHT + 2;

      // ============ CHART 5: CLUSTERED COLUMN - Shipping Performance (FULL WIDTH) ============
      console.log("📦 Creating Chart 5: Shipping Performance Analysis");

      const shippingCoIdx = columns.indexOf("shipping_co");

      const shippingStats: Record<string, { revenue: number; orders: number }> = {};

      for (const row of validRows) {
        const carrier = getColumnValue(row, shippingCoIdx, "Unknown");
        const revenue = getNumericValue(row, salePriceIdx, 0);

        if (!shippingStats[carrier]) {
          shippingStats[carrier] = { revenue: 0, orders: 0 };
        }

        shippingStats[carrier].revenue += revenue;
        shippingStats[carrier].orders += 1;
      }

      const shippingHeaders = [["Carrier", "Revenue", "Orders"]];
      const shippingDataValues = Object.entries(shippingStats)
        .sort((a, b) => b[1].revenue - a[1].revenue)
        .slice(0, 10)
        .map(([carrier, stats]) => [
          carrier.substring(0, 20) + (carrier.length > 20 ? "..." : ""),
          stats.revenue,
          stats.orders,
        ]);

      const shippingDataRows = shippingDataValues.length;
      const shippingEndRow = 7 + shippingDataRows;

      const clearRange5 = dashboardSheet.getRange(`AN7:AP${shippingEndRow + 10}`);
      clearRange5.clear(Excel.ClearApplyTo.all);
      await context.sync();

      await deleteOldTable(dashboardSheet, "ShippingData", context);
      await deleteOldChart(dashboardSheet, "ShippingPerformanceChart", context);

      dashboardSheet.getRange("AN7:AP7").values = shippingHeaders;
      dashboardSheet.getRange(`AN8:AP${shippingEndRow}`).values = shippingDataValues;

      const shippingTableRange = dashboardSheet.getRange(`AN7:AP${shippingEndRow}`);
      const shippingTable = dashboardSheet.tables.add(shippingTableRange, true);
      shippingTable.name = "ShippingData";
      shippingTable.style = "TableStyleLight1";

      const shippingHeaderRange = dashboardSheet.getRange("AN7:AP7");
      shippingHeaderRange.format.fill.color = "#E7E6E6";
      shippingHeaderRange.format.font.bold = true;
      shippingHeaderRange.format.font.size = 10;

      await context.sync();

      const shippingChart = dashboardSheet.charts.add(
        Excel.ChartType.columnClustered,
        shippingTable.getRange(),
        Excel.ChartSeriesBy.columns
      );
      shippingChart.name = "ShippingPerformanceChart";
      shippingChart.title.text = "Shipping Carrier Performance";
      shippingChart.setPosition(`A${currentRow}`, `H${currentRow + ROW_HEIGHT}`);
      shippingChart.axes.categoryAxis.title.text = "Shipping Carrier";
      shippingChart.axes.valueAxis.title.text = "Revenue / Orders";
      shippingChart.legend.visible = true;
      shippingChart.legend.position = Excel.ChartLegendPosition.bottom;

      await context.sync();
      console.log("✅ Chart 5: Shipping Performance created");

      console.log("✅ All 5 Charts Created Successfully with Proper Spacing!");

      const chartEndRow = currentRow + ROW_HEIGHT;

      // ============ DATA TABLE SECTION ============
      const dataTableStartRow = chartEndRow + 5;

      const headers = [
        ["SKU", "Product Title", "Revenue", "Units Sold", "Avg Price", "Order Count", "Trend"],
      ];

      const headerRange = dashboardSheet.getRange(`B${dataTableStartRow}:H${dataTableStartRow}`);
      headerRange.values = headers;
      headerRange.format.fill.color = "#4472C4";
      headerRange.format.font.color = "white";
      headerRange.format.font.bold = true;

      const productSummary: {
        [product: string]: { revenue: number; units: number; orderCount: number };
      } = {};

      for (const row of validRows) {
        const product = getColumnValue(row, titleIdx, "Unknown");
        const revenue = getNumericValue(row, salePriceIdx, 0);
        const units = getNumericValue(row, qtySoldIdx, 0);

        if (!productSummary[product]) {
          productSummary[product] = { revenue: 0, units: 0, orderCount: 0 };
        }

        productSummary[product].revenue += revenue;
        productSummary[product].units += units;
        productSummary[product].orderCount += 1;
      }

      const summaryRows = Object.entries(productSummary)
        .sort((a, b) => b[1].revenue - a[1].revenue)
        .slice(0, 50)
        .map(([product, stats]) => {
          const avgPrice = stats.units > 0 ? stats.revenue / stats.units : 0;
          const shortProduct = product.substring(0, 25) + (product.length > 25 ? "..." : "");
          return [
            shortProduct,
            product.substring(0, 60) + (product.length > 60 ? "..." : ""),
            stats.units,
            avgPrice,
            stats.orderCount,
            "",
          ];
        });

      const summaryStartRow = dataTableStartRow + 1;
      const summaryRange = dashboardSheet.getRange(
        `B${summaryStartRow}:H${summaryStartRow + summaryRows.length - 1}`
      );
      summaryRange.values = summaryRows;

      const revenueColRange = dashboardSheet.getRange(
        `D${summaryStartRow}:D${summaryStartRow + summaryRows.length - 1}`
      );
      revenueColRange.numberFormat = [["$#,##0.00"]];
      const avgPriceColRange = dashboardSheet.getRange(
        `F${summaryStartRow}:F${summaryStartRow + summaryRows.length - 1}`
      );
      avgPriceColRange.numberFormat = [["$#,##0.00"]];

      const monthsForSparkline = (() => {
        if (dateOfSaleIdx < 0) return [];

        const allMonths = Array.from(
          new Set(
            validRows
              .map((row) => {
                const dateValue = getColumnValue(row, dateOfSaleIdx);
                const d = parseDateValue(dateValue);
                if (!d) return null;
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              })
              .filter((m) => m !== null)
          )
        ).sort();
        return allMonths.slice(-6);
      })();

      const revenueByProductByMonth: Record<string, Record<string, number>> = {};
      for (const [product] of Object.entries(productSummary)) {
        revenueByProductByMonth[product] = {};
        for (const month of monthsForSparkline) {
          revenueByProductByMonth[product][month] = 0;
        }
      }

      if (dateOfSaleIdx >= 0) {
        for (const row of validRows) {
          const product = getColumnValue(row, titleIdx, "Unknown");
          const dateValue = getColumnValue(row, dateOfSaleIdx);

          if (dateValue) {
            try {
              const month = getMonthString(dateValue);
              const revenue = getNumericValue(row, salePriceIdx, 0);
              if (revenueByProductByMonth[product] && monthsForSparkline.includes(month)) {
                revenueByProductByMonth[product][month] += revenue;
              }
            } catch {
              // Skip invalid dates
            }
          }
        }
      }

      for (let i = 0; i < summaryRows.length; i++) {
        const shortProduct = summaryRows[i][0];
        const fullProduct = summaryRows[i][1];
        const rowNum = summaryStartRow + i;

        const revenueVals = monthsForSparkline.map(
          (month) => revenueByProductByMonth[fullProduct]?.[month] ?? 0
        );

        if (revenueVals.length > 0) {
          dashboardSheet.getRange(`H${rowNum}`).values = [[createInlineSparkline(revenueVals)]];
          dashboardSheet.getRange(`H${rowNum}`).format.font.color = "#4472C4";
          dashboardSheet.getRange(`H${rowNum}`).format.font.size = 14;
        }
      }

      await context.sync();

      dashboardSheet.getRange("AA:AZ").format.columnWidth = 40;

      const chartDataNote = dashboardSheet.getRange("AA6");
      chartDataNote.values = [["Chart Data"]];
      chartDataNote.format.font.italic = true;
      chartDataNote.format.font.size = 8;
      chartDataNote.format.font.color = "#666666";

      dashboardSheet.getRange("4:6").format.rowHeight = 30;
      dashboardSheet.getRange("A:H").format.columnWidth = 100;

      await context.sync();
      console.log("✅ Dashboard creation completed successfully!");
    });
  } catch (error) {
    console.error("❌ Error creating dashboard:", error);
    throw error;
  }
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function calculateGrowth(current: number, previous: number): string {
  const growth = ((current - previous) / previous) * 100;
  return growth >= 0 ? `+${growth.toFixed(1)}%` : `${growth.toFixed(1)}%`;
}

export async function createEnhancedSalesPerformanceDashboardWithParams(params: {
  skus: string[];
  start_purchase_date: string;
  end_purchase_date: string;
}): Promise<void> {
  try {
    await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASH_FILTER, JSON.stringify(params));
  } catch (e) {
    console.warn("Unable to persist dash_filter params; proceeding without storage.", e);
  }
  await createEnhancedSalesPerformanceDashboard();
}

export async function monitorAndRepairCharts(): Promise<void> {
  return;
}
